import {
  LUZMO_TOKEN_ENDPOINT,
  LUZMO_REPORTS_ENDPOINT,
  LUZMO_EXPORT_REPORT_ENDPOINT,
  LUZMO_SCHEDULE_REPORT_ENDPOINT
} from 'config';
import { callApi, callApiBlob } from 'services/api';
export const fetchLuzmoEmbedToken = async selectedTeam => {
  try {
    const body = await callApi(LUZMO_TOKEN_ENDPOINT, 'POST', selectedTeam ? { selectedTeam } : null);
    const expiry = new Date(body?.expiry).getTime();

    if (!body?.authKey || !body?.authToken || !Number.isFinite(expiry) || expiry <= Date.now()) {
      throw new Error('The Insights API returned an invalid or expired embed token.');
    }

    if (!body.dashboardId || !Array.isArray(body.allowedDashboardIds) ||
        !body.allowedDashboardIds.includes(body.dashboardId) ||
        !Array.isArray(body.availableTeams) || !body.availableTeams.length ||
        body.availableTeams.some(team => typeof team !== 'string' || !team.trim())) {
      throw new Error('The Insights API must provide dashboard access and permissions.');
    }
    if (body.selectedTeam !== (selectedTeam || null)) {
      throw new Error('The Insights API did not apply the selected team.');
    }
    return body;
  } catch (error) {
    const message = error?.message ||
      (typeof error?.error === 'string' ? error.error : null) ||
      'Unable to load the Luzmo embed token.';
    throw new Error(message);
  }
};

export const fetchLuzmoReports = async () => {
  try {
    // The authenticated gateway supplies the organization, not the browser.
    const body = await callApi(LUZMO_REPORTS_ENDPOINT);
    if (!Array.isArray(body?.reports)) {
      throw new Error('The reports API returned an invalid response.');
    }
    return body.reports;
  } catch (error) {
    throw new Error(error?.message || error?.error || 'Unable to load reports.');
  }
};

const parseFilename = (contentDisposition, fallback) => {
  const match = /filename="([^"]+)"|filename=([^;\s]+)/i.exec(contentDisposition || '');
  return match?.[1] || match?.[2] || fallback;
};

export const exportLuzmoReport = async ({ reportId, format, selectedTeam }) => {
  try {
    const { body: blob, headers } = await callApiBlob(LUZMO_EXPORT_REPORT_ENDPOINT, 'POST', {
      reportId,
      format,
      ...(selectedTeam ? { selectedTeam } : {})
    });
    return {
      blob,
      filename: parseFilename(headers['Content-Disposition'], `${reportId}.${format}`)
    };
  } catch (error) {
    throw new Error(error?.message || error?.error || 'Unable to export this report.');
  }
};

export const scheduleLuzmoReport = async ({
  reportId,
  frequency,
  format,
  email,
  selectedTeam
}) => {
  try {
    return await callApi(LUZMO_SCHEDULE_REPORT_ENDPOINT, 'POST', {
      reportId,
      frequency,
      format,
      ...(selectedTeam ? { selectedTeam } : {}),
      ...(email?.trim() ? { email: email.trim() } : {})
    });
  } catch (error) {
    throw new Error(error?.message || error?.error || 'Unable to schedule this report.');
  }
};

export const fetchLuzmoSchedules = async () => {
  try {
    const body = await callApi(LUZMO_SCHEDULE_REPORT_ENDPOINT);
    if (!Array.isArray(body?.rows)) {
      throw new Error('The schedules API returned an invalid response.');
    }
    return body.rows;
  } catch (error) {
    throw new Error(error?.message || error?.error || 'Unable to load scheduled reports.');
  }
};

export const updateLuzmoSchedule = async ({ id, frequency }) => {
  try {
    return await callApi(
      `${LUZMO_SCHEDULE_REPORT_ENDPOINT}/${encodeURIComponent(id)}`,
      'PATCH',
      { frequency }
    );
  } catch (error) {
    throw new Error(error?.message || error?.error || 'Unable to update this schedule.');
  }
};

export const deleteLuzmoSchedule = async id => {
  try {
    return await callApi(`${LUZMO_SCHEDULE_REPORT_ENDPOINT}/${encodeURIComponent(id)}`, 'DELETE');
  } catch (error) {
    throw new Error(error?.message || error?.error || 'Unable to delete this schedule.');
  }
};

// Keep a team selection when navigating between Insights dashboards/reports.
export const withInsightsTeam = (target, selectedTeam) => {
  if (!selectedTeam) return target;
  const url = new URL(target, window.location.origin);
  if (url.origin !== window.location.origin ||
      !/^\/area\/ai-insights(?:\/|$)/.test(url.pathname)) return target;
  if (!url.searchParams.has('team')) url.searchParams.set('team', selectedTeam);
  return url.pathname + url.search + url.hash;
};
